/* eslint-disable unicorn/consistent-destructuring */
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTrans } from 'hooks'
import { Accordion, Breadcrumbs, JsonLd, Tag, WContainer, WImage, WLink } from '@components/atoms'
import {
  Page,
  PDPBanner,
  PDPInfoSlider,
  ProdottiCorrelati,
  TextImageLarge,
  VideoModal,
} from '@components/organisms'
import { ProdottoNode, usePromoBundleProdottoQuery } from '@gql/graphql'
import styles from './ProdottoDetailTemplate.module.sass'
import { PDPDetail, PDPDownloadCard, PDPInfoCard } from '@components/molecules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { A11y, Navigation } from 'swiper/modules'
import { Card, Play, Return, Sicurezza, Truck } from '@components/icons'
import { CHIAVE_PLUS } from '@utils/settings'
import PDPBar from '@components/molecules/PDPBar/PDPBar'
import { isEcommerce, isFiliale } from '@utils/index'
import { useRouter } from 'next/router'
import { formatStringToHtml, prezziProdotto } from '@utils/safe'
import groupBy from 'lodash/groupBy'
import { CartContext, initialCart } from '@utils/cartContext'
import { handleProductDetailTracking } from '@utils/tracking'
// import { useRecommendation } from '@hooks/useRecommendation' // TODO: RECOMMENDATION
// import useCookie from '@hooks/useCookie' // TODO: RECOMMENDATION

interface Props {
  pagina?: any
  prodotto?: ProdottoNode
  loading?: boolean
}

const ProdottoDetailTemplate = (props: Props) => {
  const { pagina = {}, prodotto = undefined, loading = false } = props

  const router = useRouter()

  const t = useTrans()
  const { cart, loading: cartLoading, addToCart: contextAddToCart } = useContext(CartContext)

  const ecommerce = isEcommerce(router.locale)
  const filiale = isFiliale(router.locale)

  const pdpDetailRef = useRef<HTMLDivElement>(null)
  const [pdpBarVisible, setPdpBarVisible] = useState(false)
  const [videoOpen, setVideoOpen] = useState(undefined)
  const [hasTracked, setHasTracked] = useState(false)
  // const { recommendedForYou } = useRecommendation(prodotto) // TODO: RECOMMENDATION
  // const [lastProductViewed, setLastProductViewed] = useCookie('lastProductViewed', prodotto.codice) // TODO: RECOMMENDATION

  const domainLocales = router.domainLocales
  const locale = router.locale

  let canale =
    domainLocales?.find((x) => x.defaultLocale === locale) ||
    domainLocales?.find((x) => x.defaultLocale === 'default')
  const domain = canale?.domain

  // useEffect(() => {
  //   setLastProductViewed(prodotto.codice)
  // }, [prodotto.codice, setLastProductViewed]) // TODO: RECOMMENDATION

  const { data } = usePromoBundleProdottoQuery({
    variables: { pk: prodotto.pk },
    fetchPolicy: 'cache-first',
  })

  const handleScroll = () => {
    if (pdpDetailRef.current) {
      const scrollPosition = window.scrollY
      const targetOffsetTop = pdpDetailRef.current.offsetTop + pdpDetailRef.current.offsetHeight

      if (scrollPosition > targetOffsetTop) {
        setPdpBarVisible(true)
      } else {
        setPdpBarVisible(false)
      }
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    trackViewItem()
  }, [cart])

  const trackViewItem = () => {
    if (prodotto && (process.env.SITE_ID == '3' || process.env.SITE_ID == '4') && !hasTracked) {
      handleProductDetailTracking(prodotto, cart)
      setHasTracked(true)
    }

    if (prodotto && cart && !cartLoading && cart !== initialCart && !hasTracked) {
      handleProductDetailTracking(prodotto, cart)
      setHasTracked(true)
    }
  }

  const promoBundle = data?.prodottoByPk?.promoBundle

  const prodottiCompatibili = groupBy(
    prodotto.prodottiCompatibili,
    (product) => product.categoriaCompatibili?.boxTitolo || product.categoriaCompatibili?.nome
  )
  const showCompatibili =
    (Object.keys(prodottiCompatibili).length > 0 && prodotto.isAccessorio) ||
    (prodotto.categorieCorrelate?.length > 0 && prodotto.isAbbigliamento)

  const images =
    prodotto.detailImages?.map(({ pk, image }, index) => ({
      pk,
      url: image,
      alt: `${prodotto.categoria?.boxTitolo} ${prodotto.nome}_${index + 1}`,
    })) || []

  const videos =
    prodotto.videoImages?.map(({ pk, descrizione }, index) => ({
      pk,
      url: `https://img.youtube.com/vi/${descrizione}/maxresdefault.jpg`,
      alt: `Video ${prodotto.categoria?.boxTitolo} ${prodotto.nome}_${index + 1}`,
      idYoutube: descrizione,
    })) || []

  const specificheAcquisto = [
    {
      pk: 1,
      icon: <Truck />,
      name: t('Spedizione Gratuita'),
      description: t('su molti prodotti con una spesa minima di 30€'),
    },
    {
      pk: 2,
      icon: <Card />,
      name: t('Pagamenti sicuri'),
      description: t('con Bonifico, Carte di Credito e PayPal'),
    },
    {
      pk: 3,
      icon: <Sicurezza />,
      name: t('Garanzia Prodotto'),
      description: t('estensibile gratis fino a 5 anni'),
    },
    {
      pk: 4,
      icon: <Return />,
      name: t('Reso entro 14 giorni'),
      description: t('dal ricevimento del prodotto acquistato'),
    },
  ]

  const accordions = [
    ...(prodotto.descrizione || prodotto.plusPoints.length > 0
      ? [
          {
            key: 'info',
            label: t('Informazioni prodotto'),
            content: [
              {
                type: 'text',
                value:
                  prodotto.isAbbigliamento || prodotto.isAccessorio
                    ? formatStringToHtml(prodotto.descrizione)
                    : prodotto.descrizione,
              },
              { type: 'plusPoints', value: prodotto.plusPoints },
            ],
          },
        ]
      : []),
    {
      key: 'tech',
      label: t('Caratteristiche tecniche'),
      content: [
        {
          type: 'table',
          value: prodotto.attributi
            .filter(
              (x) =>
                !['dato_scheda_tecnica', 'tipo_scheda_tecnica', 'vista_scheda_tecnica'].includes(
                  x.chiave
                )
            )
            .filter((x) => !x.valori.some((v) => v.image))
            .map((v) => ({
              label: v.valori[0].attributo?.nome,
              value: v.valori.map((v) => v.nome).join(', '),
            }))
            .concat(
              prodotto.peso
                ? [
                    {
                      label: t('Peso singolo articolo completo compreso di imballo'),
                      value: `${prodotto.peso.toString().replace('.', ',')} kg`,
                    },
                  ]
                : []
            ),
        },
        {
          type: 'techTables',
          value:
            prodotto.attributi.filter((x) =>
              ['dato_scheda_tecnica', 'tipo_scheda_tecnica', 'vista_scheda_tecnica'].includes(
                x.chiave
              )
            ).length === 3
              ? prodotto.attributi
                  .filter((x) =>
                    ['dato_scheda_tecnica', 'tipo_scheda_tecnica', 'vista_scheda_tecnica'].includes(
                      x.chiave
                    )
                  )
                  .reduce((res, x) => ({ ...res, [x.chiave]: x }), {})
              : undefined,
        },
        {
          type: 'text',
          value:
            ecommerce && prodotto.notaRuote
              ? t(
                  'Numero di iscrizione al Registro RENAP PFU250100397SR ai sensi del D.M. 147/2024, Art. 3, comma 8'
                )
              : undefined,
        },
        {
          type: 'text',
          // Il codice è visible solo negli accessori - richiesta cliente
          // Il codice è diventato visibile per tutti i prodotti in versione ecommerce esclusi abbigliamento - richista cliente 23/05/2025
          value:
            ecommerce && prodotto.rif && !prodotto.isAbbigliamento
              ? t('Codice: {0}', [prodotto.rif])
              : undefined,
        },
        {
          type: 'loghi',
          value: [
            prodotto.use,
            ...prodotto.attributi
              .filter((x) => x.chiave === CHIAVE_PLUS && x.valori.some((v) => v.image))
              .map((x) => x.valori)
              .flat(),
          ].filter((x) => !!x?.image),
        },
        {
          type: 'text',
          value: t(
            "Dati e figure non sono vincolanti per le caratteristiche dei materiali forniti in quanto i prodotti sono soggetti a continui miglioramenti tecnici. I dati riguardanti il pratico impiego dell'equipaggiamento e quelli delle caratteristiche tecniche, così come descritti, possono subire variazioni senza preavviso."
          ),
        },
      ].filter((x) => (Array.isArray(x.value) ? !!x.value.length : !!x.value)),
    },
    ...(showCompatibili
      ? [
          {
            key: 'compatibili',
            label: t('Prodotti compatibili'),
            content: prodotto.isAccessorio
              ? Object.entries(prodottiCompatibili).map(([categoria, prodotti]) => ({
                  type: 'html',
                  value: (
                    <div className={styles.accordionContent}>
                      {categoria}
                      <br />
                      {prodotti.map((p, index) => (
                        <WLink
                          href={p.url}
                          key={p.nome}
                          // onClick={() => console.log('PRODOTTO COMPATIBILE', p)}
                        >
                          {p.nome}
                          {index < prodotti.length - 1 && ', '}
                        </WLink>
                      ))}
                    </div>
                  ),
                }))
              : [
                  {
                    type: 'html',
                    value: (
                      <div className={styles.accordionContent}>
                        {t('Abbigliamento consigliato per:')}
                        <br />
                        {prodotto.categorieCorrelate.map((c, index) => (
                          <WLink href={c.url} key={c.nome}>
                            {c.boxTitolo}
                            {index < prodotto.categorieCorrelate.length - 1 && ', '}
                          </WLink>
                        ))}
                      </div>
                    ),
                  },
                ],
          },
        ]
      : []),
    ...(prodotto.volantini && prodotto.volantini.length > 0
      ? [
          {
            key: 'volantini',
            label: t('Volantini digitali'),
            content: [
              {
                type: 'html',
                value: (
                  <>
                    <PDPInfoSlider volantini={prodotto.volantini} className={styles.infoSlider} />
                  </>
                ),
              },
            ],
          },
        ]
      : []),
    ...(prodotto.documenti.length > 0
      ? [
          {
            key: 'manuali',
            label: prodotto.isLubrificante
              ? t('Schede di sicurezza')
              : prodotto.isAccessorio || prodotto.isAbbigliamento
              ? t('Schede tecniche')
              : t("Manuali d'uso"),
            content: [{ type: 'downloads', value: prodotto.documenti }],
          },
        ]
      : []),

    ecommerce &&
      prodotto.prodottiTendina &&
      prodotto.prodottiTendina.length > 0 &&
      prodotto.isAbbigliamento &&
      prodotto.showTaglia && {
        key: 'taglie',
        label: t('Taglie'),
        content: [
          {
            type: 'table',
            value: [
              {
                chiave: prodotto.pk,
                label: t('Taglia'),
                value: t('Codice'),
              },
              ...prodotto.prodottiTendina
                ?.filter((x) => x.labelTendina)
                .map((x) => ({
                  chiave: x.pk,
                  label: x.labelTendina,
                  value: x.rif,
                })),
            ],
          },
        ],
      },
    (!prodotto.prodottiTendina || prodotto.prodottiTendina.length == 0) &&
      prodotto.isAbbigliamento &&
      prodotto.tabellaMotori &&
      prodotto.tabellaMotori?.testo && {
        key: 'taglie',
        label: t('Taglie'),
        content: [
          {
            type: 'html',
            value: (
              <div
                className={`${styles.disponibilitaMessage} ${styles['disponibilitaMessage--noecommerce']}`}
                dangerouslySetInnerHTML={{ __html: prodotto.tabellaMotori.testo }}
              />
            ),
          },
        ],
      },
    // se in caratteristiche tecniche (tech) c'è solo un elemento (che sarà il text finale che c'è sempre), non visualizzo l'intero accordion
  ].filter((x) => !!x?.content?.length && (x.key !== 'tech' || x.content?.length > 1))

  const pp = prezziProdotto(prodotto)
  const prezzi = [
    ...(ecommerce
      ? [
          { key: 'listino', label: t('Listino'), value: pp?.prezzoListino },
          { key: 'prev', label: t('Prima era'), value: pp?.prezzoPrima },
        ]
      : []),
    ...(filiale || ecommerce ? [{ key: 'current', value: pp.prezzoFinale }] : []),
  ].filter((x) => !!x.value)

  useEffect(() => {
    if (!window.omnisend) {
      return
    }

    const finalPrice = prezzi.find((p) => p.key === 'current')?.value || 0
    const oldPrice = prezzi.find((p) => p.key === 'prev')?.value || 0

    window.omnisend.push(['brandID', process.env.OMNISEND_ACCOUNT_ID])
    window.omnisend.push([
      'track',
      'viewed product',
      {
        origin: 'api',
        eventVersion: 'v4',
        properties: {
          product: {
            id: prodotto.pk.toString(),
            currency: 'EUR',
            price: finalPrice,
            oldPrice: oldPrice,
            title: prodotto?.nome,
            imageUrl: prodotto?.detailImages?.[0]?.image,
            url: `${window.location.origin}${window.location.pathname}`,
          },
        },
      },
    ])
  }, [])

  const getTags = (variant: string) =>
    prodotto.novita || prodotto.promo || promoBundle || prodotto.lancio ? (
      <div className={`${styles.tags} ${styles[`tags--${variant}`]}`}>
        {prodotto.novita && <Tag variant="black" label={t('Novità')} />}
        {prodotto.promo || promoBundle ? (
          <Tag
            variant="promo"
            label={t(promoBundle ? 'Promo bundle' : 'Promo')}
            helper={{
              id: `prodottocard_promo_helper_${prodotto.pk}`,
              label: promoBundle ? promoBundle.descrizioneProdotto : t('prodottocard_promo_helper'),
            }}
          />
        ) : prodotto.lancio ? (
          <Tag variant="promo" label={t('Prezzo lancio')} />
        ) : (
          <></>
        )}
      </div>
    ) : (
      <></>
    )

  return (
    <Page pagina={pagina} item={prodotto} className={`${styles.root}`}>
      {prodotto.disponibile && ecommerce && (
        <PDPBar prodotto={prodotto} prezzi={prezzi} visible={pdpBarVisible} />
      )}
      <main>
        <div className={styles.head}>
          <WContainer>
            <Breadcrumbs breadcrumbs={pagina.breadcrumbs} showLast />
          </WContainer>
        </div>
        <WContainer>
          <div className={styles.layout}>
            <div className={styles.imageWrap}>
              <div className={styles.imageList}>
                {getTags('desktop')}
                {images.map((x, index) => (
                  <WImage
                    src={x.url}
                    alt={x.alt}
                    maxWidth={1920}
                    maxHeight={720}
                    key={x.pk}
                    loading={index ? 'lazy' : 'eager'}
                    priority={!index}
                  />
                ))}
                {videos.map((x) => (
                  <button
                    type="button"
                    aria-label={t('Guarda video')}
                    className={styles.videoTrigger}
                    onClick={() => setVideoOpen(x)}
                  >
                    <WImage
                      src={x.url}
                      alt={x.alt}
                      maxWidth={1920}
                      maxHeight={720}
                      key={x.pk}
                      loading="lazy"
                    />
                    <span className={styles.videoTriggerPlay}>
                      <Play />
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <Swiper
              modules={[A11y, Navigation]}
              navigation
              loop
              className={`${styles.imageSlider} swiper--arrowNav`}
            >
              {images.map((x, index) => (
                <SwiperSlide key={x.pk}>
                  <WImage
                    src={x.url}
                    alt={x.alt}
                    maxWidth={768}
                    maxHeight={270}
                    objectFit="contain"
                    loading={index ? 'lazy' : 'eager'}
                    priority={!index}
                  />
                </SwiperSlide>
              ))}
              {videos.map((x) => (
                <SwiperSlide key={x.pk}>
                  <button
                    type="button"
                    aria-label={t('Guarda video')}
                    className={styles.videoTrigger}
                    onClick={() => setVideoOpen(x)}
                  >
                    <WImage
                      src={x.url}
                      alt={x.alt}
                      maxWidth={768}
                      maxHeight={270}
                      objectFit="contain"
                      key={x.pk}
                      loading="lazy"
                    />
                    <span className={styles.videoTriggerPlay}>
                      <Play />
                    </span>
                  </button>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className={styles.details}>
              {getTags('mobile')}
              <div ref={pdpDetailRef}>
                <PDPDetail
                  prodotto={prodotto}
                  loading={loading}
                  showCompatibili={showCompatibili}
                />
              </div>
              {ecommerce && !!specificheAcquisto?.length && !!prodotto.disponibile && (
                <div className={styles.purchaseSpecsList}>
                  {specificheAcquisto.map((x) => (
                    <div className={styles.purchaseSpecsItem} key={x.pk}>
                      {!!x.icon && <span className={styles.purchaseSpecsItemIcon}>{x.icon}</span>}
                      <div className={styles.purchaseSpecsItemContent}>
                        <span className={styles.purchaseSpecsItemTitle}>{x.name}</span>{' '}
                        <span className={styles.purchaseSpecsItemDescription}>{x.description}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!!promoBundle && <PDPBanner promozione={promoBundle} />}
              {!!accordions?.length && (
                <div className={styles.accordions}>
                  {accordions.map((a) => (
                    <div className={styles.accordion} key={a.key}>
                      <Accordion key={a.key} id={a.key} label={a.label} tag="h2">
                        <div className={styles.accordionMain}>
                          {a.content
                            .filter((c) => !!c.value)
                            .map((c) => (
                              <>
                                {c.type === 'table' && (
                                  <dl className={styles.table}>
                                    {c.value.map((tr) => (
                                      <div className={styles.tableRow} key={tr.chiave}>
                                        <dt className={styles.tableRowLabel}>{tr.label}</dt>
                                        <dd className={styles.tableRowValue}>{tr.value}</dd>
                                      </div>
                                    ))}
                                  </dl>
                                )}
                                {c.type === 'techTables' && (
                                  <div className={styles.tech}>
                                    {/* Ci possono essere più viste per varie versioni, es: https://www.mybertolini.it/it-it/p/401-s-11276/ */}
                                    {c.value.vista_scheda_tecnica.valori
                                      .filter((v) =>
                                        c.value.tipo_scheda_tecnica.valori.some(
                                          (t) =>
                                            t.parent?.pk === v.pk &&
                                            c.value.dato_scheda_tecnica.valori.some(
                                              (d) => d.parent?.pk === t.pk
                                            )
                                        )
                                      )
                                      .map((v) => (
                                        <div className={styles.techVista}>
                                          {c.value.vista_scheda_tecnica.valori.length > 1 && (
                                            <p className={styles.techVistaTitle}>
                                              {v.nome.toUpperCase()}
                                            </p>
                                          )}
                                          <div className={styles.techVistaList}>
                                            {c.value.tipo_scheda_tecnica.valori
                                              .filter((t) => t.parent?.pk === v.pk)
                                              .map((t) =>
                                                c.value.dato_scheda_tecnica.valori
                                                  .filter((d) => d.parent?.pk === t.pk)
                                                  .map((d) => (
                                                    <div className={styles.techTable}>
                                                      {!!t.nome && (
                                                        <p className={styles.techTableTitle}>
                                                          {t.nome}
                                                        </p>
                                                      )}
                                                      {d.nome.split('$').length > 1 && (
                                                        <p className={styles.techTableDescription}>
                                                          {d.nome.split('$')[1]}
                                                        </p>
                                                      )}
                                                      {t.image && (
                                                        <div className={styles.techTableImage}>
                                                          <WImage
                                                            src={t.image}
                                                            maxWidth={220}
                                                            maxHeight={220}
                                                            alt={t.nome}
                                                            title={t.nome}
                                                          />
                                                        </div>
                                                      )}
                                                      <table
                                                        cellPadding={0}
                                                        cellSpacing={0}
                                                        className={styles.techTableTable}
                                                      >
                                                        <tbody>
                                                          {(d.nome.split('$').length > 1
                                                            ? d.nome.split('$')?.[2].split('|')
                                                            : d.nome.split('|')
                                                          ).map((row) => (
                                                            <tr>
                                                              {row.split(':').map((col) => (
                                                                <td>{col}</td>
                                                              ))}
                                                            </tr>
                                                          ))}
                                                        </tbody>
                                                      </table>
                                                    </div>
                                                  ))
                                              )}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                )}
                                {c.type === 'text' && (
                                  <div
                                    dangerouslySetInnerHTML={{ __html: c.value }}
                                    className={styles.accordionContent}
                                  />
                                )}
                                {c.type === 'html' && c.value}
                                {c.type === 'loghi' && (
                                  <div className={styles.loghi}>
                                    {c.value.map((l) => (
                                      <figure className={styles.loghiItem} key={l?.pk}>
                                        <WImage
                                          src={l?.image}
                                          maxWidth={68}
                                          maxHeight={68}
                                          objectFit="contain"
                                          alt={l?.nome}
                                          title={l?.nome}
                                        />
                                        <figcaption className={styles.loghiItemDescription}>
                                          {l?.nome}
                                        </figcaption>
                                      </figure>
                                    ))}
                                  </div>
                                )}
                                {c.type === 'plusPoints' && (
                                  <PDPInfoSlider
                                    plusPoints={c.value}
                                    className={styles.infoSlider}
                                  />
                                )}
                                {c.type === 'downloads' && (
                                  <div className={styles.downloads}>
                                    {c.value.map((d) => (
                                      <PDPDownloadCard
                                        label={`${d.nomeDocumento} - ${d.lingue
                                          ?.map((x) => x.codice)
                                          ?.join(' - ')}`}
                                        url={d.url}
                                        key={d.pk}
                                        manuale={true}
                                      />
                                    ))}
                                  </div>
                                )}
                              </>
                            ))}
                        </div>
                      </Accordion>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </WContainer>
        <div className={styles.bottom}>
          <ProdottiCorrelati
            titolo={
              process.env.SITE_ID == '3' || process.env.SITE_ID == '4'
                ? t('Potrebbero interessarti anche')
                : t('Spesso acquistati insieme')
            }
            prodotti={prodotto.boughtTogether}
          />

          {/* 
          Commentato per ABTEST recommendation, ripristinare in seguito TODO
          <ProdottiCorrelati
            titolo={t('Consigliati per te')}
            prodotti={recommendedForYou.length > 0 ? recommendedForYou : prodotto.suggestedForYou}
            key={`recommendedForYou-${recommendedForYou.length}`}
          /> 
          */}
          {/* <div id="consigliati_per_te_ai">
            <ProdottiCorrelati
              titolo={t('Consigliati per te')}
              prodotti={recommendedForYou}
              key={`recommendedForYou-${recommendedForYou.length}`}
            />
          </div>
          <div id="consigliati_per_te_non_ai" className={styles.consigliatiPerTeNonAi}>
            <ProdottiCorrelati
              titolo={t('Consigliati per te')}
              prodotti={prodotto.suggestedForYou}
              key={`prodotto.suggestedForYou-${prodotto.suggestedForYou.length}`}
            />
          </div> TODO: RECOMMENDATION*/}
          <ProdottiCorrelati titolo={t('Consigliati per te')} prodotti={prodotto.suggestedForYou} />
          {ecommerce && (
            <TextImageLarge
              item={{
                pk: 1,
                image: `/${process.env.SITE}/banner_garanzia_pdp.png`,
                name: t('Per te un buono di €10'),
                description: t(`
                <p>Registra il tuo prodotto e ottieni un buono sconto di €10 utilizzabile per un successivo ordine del valore minimo di €60. Il coupon è valido per 30 giorni dal momento dell’emissione, ed è spendibile sul sito %site_domain</p>
              `).replaceAll('%site_domain', domain),
                children: [
                  {
                    pk: 1,
                    ctaLabel: t('Registra il tuo prodotto'),
                    url: '/myaccount/warranty/',
                  },
                ],
              }}
              data={t('intro_banner_garanzia_pdp')}
              caption={t(
                '<p>Lo sconto non è cumulabile con le altre promozioni in corso, più coupon di questa offerta non possono essere applicati sullo stesso acquisto.</p>'
              )}
              imagePos={'right'}
              theme={'neutrallight'}
              staticMedia
            />
          )}
        </div>
      </main>
      <VideoModal open={!!videoOpen} onClose={() => setVideoOpen(false)} video={videoOpen} />
      <JsonLd item={{ ...prodotto, descrizione: pagina.seo?.description }} type="prodotto" />
    </Page>
  )
}

export default ProdottoDetailTemplate
